import { useState } from "react"
import { Loader2, Pause, Play, Plus, Send, Square } from "lucide-react"
import { CAMPAIGN_STATUS_LABELS, SURVEY_ORIGIN_LABELS, computeNps, type CampaignSummary, type SurveyOrigin } from "../../shared/domain"
import { Empty, ErrorBox, Loading, MonthColumns, Panel } from "@/components/common"
import { CampaignDialog } from "@/components/CampaignDialog"
import { Badge } from "@/components/ui/badge"
import { Button } from "@/components/ui/button"
import { apiErrorMessage } from "@/lib/api"
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table"
import { useApp } from "@/lib/app-context"
import { fmtDate, fmtMonth, fmtNum, fmtShort, fmtSigned, useAsync } from "@/lib/format"

/**
 * Inquéritos: campanhas (criar, ativar, enviar agora), envios, respostas e
 * NPS por mês. Os envios periódicos, lembretes e o pós-fecho correm pelos
 * Fluxos (`flows/` no repo); o editor de modelos chega com a Parte 4.
 */
export function SurveysPage() {
  const { api, settings } = useApp()
  const { data, error, loading, reload } = useAsync(() => api.surveys(), [api])
  const [creating, setCreating] = useState(false)
  const [editing, setEditing] = useState<CampaignSummary | null>(null)
  const [originFilter, setOriginFilter] = useState<SurveyOrigin | "all">("all")
  const lowNps = settings.surveys.low_score_nps
  const lowAvg = settings.surveys.low_score_avg

  if (error) return <ErrorBox message={error} onRetry={reload} />
  if (loading || !data) return <Loading />

  const allNps = data.responses.map((r) => r.nps).filter((x): x is number => x != null)
  const visibleResponses = originFilter === "all" ? data.responses : data.responses.filter((r) => r.origin === originFilter)

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <div>
          <h2 className="text-base font-semibold">Inquéritos de satisfação</h2>
          <p className="text-xs text-muted-foreground">Campanhas, envios e respostas</p>
        </div>
        <Button size="sm" onClick={() => setCreating(true)}>
          <Plus className="h-4 w-4" /> Nova campanha
        </Button>
      </div>

      {data.campaigns.length === 0 ? (
        <Panel>
          <Empty>Ainda não há campanhas. Cria uma campanha periódica; a de “Após resolução” aparece no primeiro envio automático.</Empty>
        </Panel>
      ) : (
        <div className="grid gap-3 md:grid-cols-2 lg:grid-cols-3">
          {data.campaigns.map((c) => (
            <CampaignCard key={c.id} c={c} onChange={reload} onEdit={() => setEditing(c)} />
          ))}
        </div>
      )}

      <div className="grid gap-4 lg:grid-cols-3">
        <Panel title="NPS por mês" className="lg:col-span-2">
          <MonthColumns
            items={data.by_month.map((m) => ({ label: fmtMonth(m.month), value: m.nps == null ? null : m.nps + 100, hint: `${m.responses} respostas` }))}
            max={200}
            format={(v) => fmtSigned(v - 100)}
          />
        </Panel>
        <Panel title="Resumo">
          <dl className="grid grid-cols-2 gap-3 text-sm">
            <dt className="text-muted-foreground">Respostas</dt>
            <dd className="text-right tabular-nums">{data.responses.length}</dd>
            <dt className="text-muted-foreground">NPS</dt>
            <dd className="text-right tabular-nums">{fmtSigned(computeNps(allNps))}</dd>
            <dt className="text-muted-foreground">Pediram contacto</dt>
            <dd className="text-right tabular-nums">{data.responses.filter((r) => r.contact_requested).length}</dd>
          </dl>
          <p className="mt-3 text-xs text-muted-foreground">
            Avaliação baixa: NPS ≤ {lowNps} ou média ≤ {fmtNum(lowAvg)}.
          </p>
        </Panel>
      </div>

      <Panel
        title="Últimas respostas"
        action={
          <select
            className="h-8 rounded-md border bg-background px-2 text-xs"
            value={originFilter}
            onChange={(e) => setOriginFilter(e.target.value as SurveyOrigin | "all")}
            aria-label="Origem"
          >
            <option value="all">Todas as origens</option>
            {(Object.keys(SURVEY_ORIGIN_LABELS) as SurveyOrigin[]).map((o) => (
              <option key={o} value={o}>
                {SURVEY_ORIGIN_LABELS[o]}
              </option>
            ))}
          </select>
        }
      >
        {visibleResponses.length === 0 ? (
          <Empty>Sem respostas.</Empty>
        ) : (
          <div className="overflow-x-auto">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Data</TableHead>
                  <TableHead>Cliente</TableHead>
                  <TableHead>Inquérito</TableHead>
                  <TableHead>Origem</TableHead>
                  <TableHead className="text-right">NPS</TableHead>
                  <TableHead className="text-right">Média</TableHead>
                  <TableHead>Comentário</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {visibleResponses.map((r) => {
                  const low = (r.nps != null && r.nps <= lowNps) || (r.score_avg != null && r.score_avg <= lowAvg)
                  return (
                    <TableRow key={r.id}>
                      <TableCell className="tabular-nums">{fmtShort(r.submitted_at)}</TableCell>
                      <TableCell className="max-w-[12rem] truncate">{r.client_label ?? "—"}</TableCell>
                      <TableCell className="max-w-[10rem] truncate text-muted-foreground">{r.campaign_name ?? "—"}</TableCell>
                      <TableCell className="max-w-[10rem] truncate text-muted-foreground">
                        {SURVEY_ORIGIN_LABELS[r.origin]}
                        {r.origin_ref ? ` ${r.origin_ref}` : ""}
                      </TableCell>
                      <TableCell className={`text-right tabular-nums ${low ? "font-medium text-red-600" : ""}`}>{r.nps ?? "—"}</TableCell>
                      <TableCell className={`text-right tabular-nums ${low ? "font-medium text-red-600" : ""}`}>{fmtNum(r.score_avg)}</TableCell>
                      <TableCell className="max-w-[24rem] truncate text-muted-foreground">
                        {r.contact_requested ? <Badge variant="warning" className="mr-1">pediu contacto</Badge> : null}
                        {r.comment ?? ""}
                      </TableCell>
                    </TableRow>
                  )
                })}
              </TableBody>
            </Table>
          </div>
        )}
      </Panel>

      <CampaignDialog
        open={creating || !!editing}
        campaign={editing}
        onOpenChange={(v) => {
          if (!v) {
            setCreating(false)
            setEditing(null)
          }
        }}
        onSaved={() => {
          setCreating(false)
          setEditing(null)
          reload()
        }}
      />
    </div>
  )
}

function CampaignCard({ c, onChange, onEdit }: { c: CampaignSummary; onChange(): void; onEdit(): void }) {
  const { api, toast } = useApp()
  const [busy, setBusy] = useState<string | null>(null)
  const run = async (key: string, fn: () => Promise<unknown>) => {
    setBusy(key)
    try {
      await fn()
      onChange()
    } catch (e) {
      toast("error", "Não foi possível concluir", apiErrorMessage(e))
    } finally {
      setBusy(null)
    }
  }
  const sendNow = () =>
    run("send", async () => {
      const r = await api.sendCampaign(c.id)
      if (r.skipped) toast("info", r.skipped)
      else toast(r.failed ? "error" : "success", `${r.sent} enviados`, r.remaining ? `Faltam ${r.remaining}: volta a carregar em “Enviar agora”.` : r.failed ? `${r.failed} falhados.` : "Todos os destinatários receberam.")
    })
  const status = (s: string) => run(s, () => api.updateCampaign(c.id, { status: s }))
  const periodic = c.kind === "periodic"
  return (
    <div className="rounded-xl border border-border/60 bg-card p-4 shadow-sm">
      <div className="flex items-start justify-between gap-2">
        <button className="text-left text-sm font-semibold hover:underline disabled:no-underline" disabled={!periodic} onClick={onEdit}>
          {c.name}
        </button>
        <Badge variant={c.status === "active" ? "success" : c.status === "paused" ? "warning" : "neutral"}>
          {c.kind === "event" ? `Automático · ${CAMPAIGN_STATUS_LABELS[c.status]}` : CAMPAIGN_STATUS_LABELS[c.status]}
        </Badge>
      </div>
      <p className="mt-1 text-xs text-muted-foreground">
        {periodic
          ? `${c.starts_at ? `De ${fmtDate(c.starts_at)}` : "Sem início"}${c.ends_at ? ` a ${fmtDate(c.ends_at)}` : ""}${
              c.reminder_after_days ? ` · lembrete ao ${c.reminder_after_days}.º dia` : ""
            }${c.search ? ` · clientes “${c.search}”` : ""}`
          : "Enviado quando uma reclamação é encerrada"}
      </p>
      <div className="mt-3 flex items-baseline justify-between">
        <span className="text-sm">
          <span className="text-xl font-semibold tabular-nums">{c.sends.responded}</span> respostas
        </span>
        <span className="text-sm tabular-nums text-muted-foreground">{c.response_rate == null ? "—" : `${c.response_rate}%`}</span>
      </div>
      <div className="mt-2 h-1.5 rounded-full bg-secondary">
        <div className="h-1.5 rounded-full bg-accent" style={{ width: `${c.response_rate ?? 0}%` }} />
      </div>
      <p className="mt-2 text-xs text-muted-foreground">
        {c.sends.sent} enviados · {c.sends.opened} abertos{c.sends.failed ? ` · ${c.sends.failed} falhados` : ""} · NPS {fmtSigned(c.nps)}
      </p>
      {periodic ? (
        <p className="mt-1 select-all break-all font-mono text-[10px] text-muted-foreground" title="Id para o fluxo de envio periódico">
          {c.id}
        </p>
      ) : null}
      {c.status !== "closed" ? (
        <div className="mt-3 flex flex-wrap gap-1.5">
          {periodic && c.status === "active" ? (
            <Button size="sm" disabled={!!busy} onClick={sendNow}>
              {busy === "send" ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Send className="h-3.5 w-3.5" />} Enviar agora
            </Button>
          ) : null}
          {c.status === "draft" || c.status === "paused" ? (
            <Button size="sm" variant="outline" disabled={!!busy} onClick={() => status("active")}>
              <Play className="h-3.5 w-3.5" /> Ativar
            </Button>
          ) : null}
          {c.status === "active" ? (
            <Button size="sm" variant="outline" disabled={!!busy} onClick={() => status("paused")}>
              <Pause className="h-3.5 w-3.5" /> Pausar
            </Button>
          ) : null}
          {periodic ? (
            <Button size="sm" variant="ghost" disabled={!!busy} onClick={() => status("closed")}>
              <Square className="h-3.5 w-3.5" /> Terminar
            </Button>
          ) : null}
        </div>
      ) : null}
    </div>
  )
}
